// Riftline presentation, "Rising Wall Wave" (owner decision 2026-11-02; replaces RIFTLINE-VFX-01 "The Resonance Cut").
// Render-only: reads `sim.rift.walls` (authoritative wall positions) and routed hit events, and never feeds anything back
// into the simulation.
//
// The ground splits in a line across the hero's front, and a wall of living cel fire erupts from the glowing crack and
// rolls forward with the sim wall. Nova Lance is a long thin blue beam; this is a wide, burning wall that shoves the crowd.
// Owner fire pass (2026-11-02: "a real live fire look… active flames moving… it looks kind of stiff"): everything that
// burns now moves on the GPU from `time`:
//  1. Flame cards: rows of camera-facing (cylindrical billboard, top leaned back toward screen-up) flipbook fire cards along the
//     wall crest and along every hot crack. They sample the procedural 8×8 anime-fire atlas (public/textures/
//     aura_fire_atlas.png, read-only reuse from auraFlame.ts) with two scrolling noise warps, posterised into hard cel
//     bands (white-hot base → yellow → orange → red rim), and sway, stretch and flicker per card. Tongues peel off as embers.
//  2. Lead crest: four staggered rows of jagged spikes (fire, one teal energy row) with an ink hull. Vertex shader: per-spike
//     sway, stretch and flicker with phase offsets; fragment: heat bands flowing upward through noise turbulence.
//  3. Fire curtain: a forward-curling sheet behind the crest drawing the same flipbook fire, rippling as it rolls.
//  4. Trail: world-fixed spikes dropped behind the moving wall that sink and cool to ember, so it reads as rolling.
//  5. Ground: a burning crack decal at the split line (and a smaller one where the wall dies) that cools and fades over
//     ~2 s; its flame cards shrink and redden as it cools.
//  6. Embers: opaque cel sparks streaming up off the crest and the cracks with buoyancy and turbulence (readable with
//     Glow off), plus a few additive glints, dust puffs and rock chips (BlastFx pools) and small hit sparks.
// All pools are preallocated; no per-frame allocation.
import * as THREE from 'three/webgpu';
import {
  abs, attribute, cameraWorldMatrix, clamp, color, cos, dot, float, floor, fract, instancedBufferAttribute, max, mix, mod, mx_noise_float, normalize,
  normalLocal, normalWorld, positionGeometry, positionLocal, sin, smoothstep, step, texture, time, uv, vec2, vec3, vec4,
} from 'three/tsl';
import { RIFT_HIT_COLOR as SIM_RIFT_HIT_COLOR } from '../core/sim';
import type { RiftWall, Sim } from '../core/sim';
import { auraTextures, type AuraTextures } from './auraFlame';
import { CROWD, FLASH, Fx } from './fx';
import { outlineGeometry } from './toon';

/** The sim tags Riftline damage with this hit colour. The view uses it only to route hits here. */
export const RIFT_HIT_COLOR = SIM_RIFT_HIT_COLOR;

// fire ramp (ember → red → orange → amber → pale / white-hot) and the old teal identity for one spike row
export const EMBER = 0x3a0c05, RED = 0xc42a0a, ORANGE = 0xff6a14, AMBER = 0xffb52e, PALE = 0xfff0b8, WHITE_HOT = 0xfff8e4, RIM = 0x6e1004;
export const TEAL_DK = 0x0c4f4a, TEAL = 0x22c2ac, TEAL_HI = 0x9ff7e6, CHAR = 0x0c0402, INK = 0x1c0805;
const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _e = new THREE.Euler(0, 0, 0, 'YXZ');

/* eslint-disable @typescript-eslint/no-explicit-any */
export type U = any;
/** world axes (displacements scale a constant axis) */
export const UP: U = vec3(0, 1, 0), XAX: U = vec3(1, 0, 0), ZAX: U = vec3(0, 0, 1);
export const n2 = (x: U, y: U): U => mx_noise_float(vec3(x, y, float(0.37)));
export const n3 = (x: U, y: U, z: U): U => mx_noise_float(vec3(x, y, z));
export const hash1 = (i: number): number => { const s = Math.sin(i * 127.1 + 11.7) * 43758.5453; return s - Math.floor(s); };

// ---------------------------------------------------------------- shared cel fire (flipbook atlas)
/** fire flipbook atlas layout (scripts/gen-aura-textures.py): 64 frames on an 8×8 grid, a seamless loop */
const FRAMES = 64, GRID = 8;
/** Flipbook fire in fire space (fx periodic across, fy 0 at the base .. 1 at the top), cross-faded between frames and
 *  warped by two scrolling noises. phase (0..1) offsets the frame and the warp so neighbouring flames differ. */
export function fireAt(tex: AuraTextures, fx: U, fy: U, phase: U, rate: number): { heat: U; alpha: U; ember: U } {
  const w1: U = texture(tex.noise, vec2(fx.mul(0.55), fy.mul(0.8)).add(vec2(phase.mul(0.37), time.mul(-0.55)))).r.mul(2).sub(1);
  const w2: U = texture(tex.noise, vec2(fx.mul(1.1), fy.mul(1.5)).add(vec2(time.mul(0.11), time.mul(-0.95)))).g.mul(2).sub(1);
  const x = fract(fx.add(w1.mul(0.08))), y = clamp(fy.add(w2.mul(0.09)), 0.004, 0.996);
  const fr = time.mul(rate).add(phase.mul(FRAMES));
  const ff = fract(fr.div(FRAMES)).mul(FRAMES);
  const f0 = floor(ff), f1 = mod(f0.add(1), FRAMES), blend = fract(ff);
  const cell = (fi: U): U => { const col = mod(fi, GRID), row = floor(fi.div(GRID)); return vec2(col.add(x).div(GRID), float(GRID - 1).sub(row).add(y).div(GRID)); };
  const s0: U = texture(tex.fire, cell(f0)), s1: U = texture(tex.fire, cell(f1));
  return { heat: mix(s0.r, s1.r, blend), alpha: mix(s0.a, s1.a, blend), ember: mix(s0.g, s1.g, blend) };
}
/** hard cel bands: white-hot core → yellow → orange → red, with a dark red rim just inside the silhouette */
export function fireColour(heat: U, ember: U, alpha: U): U {
  let c: U = mix(color(RED), color(ORANGE), smoothstep(0.3, 0.35, heat));
  c = mix(c, color(AMBER), smoothstep(0.5, 0.55, heat));
  c = mix(c, color(WHITE_HOT), smoothstep(0.74, 0.79, heat));
  c = mix(c, color(WHITE_HOT), ember);
  return mix(c, color(RIM), float(1).sub(smoothstep(0.5, 0.66, alpha)).mul(0.8));
}
export const celAlpha = (alpha: U): U => smoothstep(0.32, 0.5, alpha);
/** horizontal camera right / back (cylindrical billboards rotate about world Y only; meshes sit at identity) */
export const FLAT: U = vec3(1, 0, 1);
export const camRight = (): U => normalize((cameraWorldMatrix as U).mul(vec4(1, 0, 0, 0)).xyz.mul(FLAT));
export const camBack = (): U => normalize((cameraWorldMatrix as U).mul(vec4(0, 0, 1, 0)).xyz.mul(FLAT));

// ---------------------------------------------------------------- geometry
/** A jagged shard: diamond base on y = 0, tip at y = 1 nudged forward (+z) and sideways, a kink half way. Flat shaded. */
export function spikeGeometry(): THREE.BufferGeometry {
  const B = [[-0.5, 0, 0], [0, 0, 0.28], [0.5, 0, 0], [0, 0, -0.28]], K = [[0.16, 0.5, 0.06], [-0.2, 0.55, -0.02]], T = [0.06, 1, 0.12];
  const P: number[] = [];
  const tri = (a: number[], b: number[], c: number[]) => P.push(...a, ...b, ...c);
  // four faces, each split at a kink vertex so the silhouette is jagged rather than a clean pyramid
  for (let i = 0; i < 4; i++) {
    const a = B[i], b = B[(i + 1) % 4], k = K[i % 2];
    tri(a, b, k); tri(b, T, k); tri(k, T, a);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(P, 3));
  g.computeVertexNormals();
  return g;
}

/** Fire curtain: x ∈ [-0.5, 0.5] across, y ∈ [0, 1] up, curling forward (+z) toward the top like a breaking wave. */
export function sheetGeometry(sx: number, sy: number): THREE.BufferGeometry {
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  for (let j = 0; j <= sy; j++) for (let i = 0; i <= sx; i++) {
    const u = i / sx, v = j / sy;
    pos.push(u - 0.5, v, 0.75 * v * v - 0.15 * v); uvs.push(u, v);
    if (i < sx && j < sy) { const a = j * (sx + 1) + i, b = a + sx + 1; idx.push(a, b, a + 1, a + 1, b, b + 1); }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3)); g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2)); g.setIndex(idx);
  return g;
}

/** Card: x ∈ [-0.5, 0.5], y ∈ [0, 1], z = 0; subdivided vertically so the sway bends it. */
export function cardGeometry(): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(1, 1, 1, 6); g.translate(0, 0.5, 0);
  return g;
}

// ---------------------------------------------------------------- pools
/** Instanced spikes written fresh every frame (lead rows + trail), with an ink hull sharing the matrices. The vertex
 *  shader sways, stretches and flickers each spike with its own phase; the colour bands flow upward. */
export class Spikes {
  readonly mesh: THREE.InstancedMesh; readonly ink: THREE.InstancedMesh;
  /** per instance: teal, heat, phase, base y */
  private k: THREE.InstancedBufferAttribute; private n = 0;
  constructor(readonly cap: number) {
    const g = spikeGeometry();
    this.k = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.k.setUsage(THREE.DynamicDrawUsage);
    const K = instancedBufferAttribute(this.k) as U, gy = positionGeometry.y;
    // displacement in world units (instancing is applied before positionNode): the tip sways in a slow loop, the whole
    // spike stretches and snaps with a fast flicker, phase-offset per spike so the row never moves in lockstep
    const ph = K.z.mul(37), h = positionLocal.y.sub(K.w).max(0), tip = gy.mul(gy);
    const sway = XAX.mul(sin(time.mul(6.1).add(ph).add(gy.mul(2.4)))).add(ZAX.mul(cos(time.mul(4.7).add(ph.mul(1.3)).add(gy.mul(1.9))))).mul(tip).mul(h.mul(0.11).add(0.06));
    const stretch = sin(time.mul(13.0).add(ph.mul(2.1))).mul(0.6).add(sin(time.mul(23.0).add(ph.mul(0.7))).mul(0.4)).mul(0.13).mul(K.y);
    const wobble: U = sway.add(UP.mul(h.mul(stretch)));
    const m = new THREE.MeshBasicNodeMaterial({ fog: false });
    m.positionNode = positionLocal.add(wobble);
    // heat bands flowing upward through turbulence: white-hot base → yellow → orange → red tip
    const flow = gy.add(n3(gy.mul(3.2).sub(time.mul(3.4)).add(K.z.mul(9)), positionGeometry.x.mul(3), K.z.mul(5)).mul(0.13));
    let fire: U = mix(color(EMBER), color(WHITE_HOT), smoothstep(0.02, 0.1, flow));
    fire = mix(fire, color(AMBER), smoothstep(0.28, 0.34, flow));
    fire = mix(fire, color(ORANGE), smoothstep(0.52, 0.58, flow));
    fire = mix(fire, color(RED), smoothstep(0.78, 0.84, flow));
    let teal: U = mix(color(TEAL_DK), color(TEAL_HI), smoothstep(0.02, 0.12, flow));
    teal = mix(teal, color(TEAL), smoothstep(0.45, 0.55, flow));
    teal = mix(teal, color(TEAL_DK), smoothstep(0.82, 0.9, flow));
    // two-tone cel facets from a fixed key light, a quick brightness flicker; heat cools sinking spikes to dark ember
    const lit = smoothstep(0.05, 0.2, dot(normalWorld, vec3(-0.45, 0.62, 0.64)));
    const flick = sin(time.mul(19).add(ph.mul(3))).mul(0.06).add(1);
    const base = mix(fire, teal, K.x).mul(lit.mul(0.3).add(0.76)).mul(flick);
    m.colorNode = mix(color(EMBER).mul(0.8), base, K.y);
    this.mesh = new THREE.InstancedMesh(g, m, cap);
    const im = new THREE.MeshBasicNodeMaterial({ side: THREE.BackSide, fog: false });
    im.colorNode = color(INK);
    im.positionNode = positionLocal.add(wobble).add(normalLocal.mul(0.045)); // hull along the welded smooth normals (toon.ts outlineGeometry)
    this.ink = new THREE.InstancedMesh(outlineGeometry(g), im, cap);
    this.ink.instanceMatrix = this.mesh.instanceMatrix;
    for (const o of [this.mesh, this.ink]) { o.frustumCulled = false; o.count = 0; }
    this.mesh.renderOrder = 2; this.ink.renderOrder = 1;
  }
  begin(): void { this.n = 0; }
  /** base at (x, y, z); yaw so local +z points along travel; lean tilts the tip forward (rad); sx across, h tall, sz deep */
  put(x: number, y: number, z: number, yaw: number, lean: number, sx: number, h: number, sz: number, teal: number, heat: number, phase: number): void {
    if (this.n >= this.cap || h < 0.03) return;
    _e.set(lean, yaw, 0); _q.setFromEuler(_e); _p.set(x, y, z); _s.set(sx, h, sz);
    this.mesh.setMatrixAt(this.n, _m.compose(_p, _q, _s));
    const K = this.k.array as Float32Array, i = this.n * 4; K[i] = teal; K[i + 1] = heat; K[i + 2] = phase; K[i + 3] = y;
    this.n++;
  }
  end(): void {
    this.mesh.count = this.ink.count = this.n;
    this.mesh.instanceMatrix.needsUpdate = true; this.k.needsUpdate = true;
  }
}

/** Camera-facing flipbook fire cards (wall crest rows and crack flames), written fresh every frame. Instance matrices
 *  are pure translations (the base centre); the vertex shader builds the billboard, leans its top back toward screen-up
 *  so it faces the 55° gameplay camera instead of foreshortening, and sways / stretches it per card. */
export class FlameCards {
  readonly mesh: THREE.InstancedMesh;
  /** a = (width, height, phase, alpha); b = (heat, lean, sway, tiles across) */
  private a: THREE.InstancedBufferAttribute; private b: THREE.InstancedBufferAttribute; private n = 0;
  constructor(readonly cap: number, tex: AuraTextures) {
    this.a = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.a.setUsage(THREE.DynamicDrawUsage);
    this.b = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); this.b.setUsage(THREE.DynamicDrawUsage);
    // two per-instance vec4s as named geometry attributes (two instancedBufferAttribute() nodes in one material aliased)
    const geo = cardGeometry(); geo.setAttribute('cardA', this.a); geo.setAttribute('cardB', this.b);
    const A = attribute('cardA', 'vec4') as U, B = attribute('cardB', 'vec4') as U, g = positionGeometry;
    const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const ph = A.z;
    // licking motion: the top sways side to side (two frequencies), the card stretches and snaps
    const H = A.y.mul(sin(time.mul(9.0).add(ph.mul(41))).mul(0.07).add(sin(time.mul(15.0).add(ph.mul(17))).mul(0.05)).add(1));
    const sway = sin(time.mul(5.3).add(ph.mul(29)).add(g.y.mul(2.6))).mul(0.6).add(sin(time.mul(8.7).add(ph.mul(13)).add(g.y.mul(4.1))).mul(0.4)).mul(B.z).mul(g.y.mul(g.y));
    const centre = positionLocal.sub(g); // instance matrix = translation only
    m.positionNode = centre.add(camRight().mul(g.x.mul(A.x).add(sway))).add(UP.mul(g.y.mul(H))).sub(camBack().mul(g.y.mul(H).mul(B.y))); // lean the top away from the camera: faces it at the 55° pitch
    // skip the frames' solid skirt a little (more tongue per card) and cut the card's base raggedly, so cards overlapping
    // along a crack or a wall don't stack into a ladder of straight bottom edges
    const f = fireAt(tex, uv().x.mul(B.w).add(ph.mul(3.7)), uv().y.mul(0.82).add(0.18), ph, 13);
    const rag = n2(uv().x.mul(5).add(ph.mul(19)), time.mul(1.5)).mul(0.12);
    const base = smoothstep(0.02, 0.16, uv().y.add(rag));
    // cooling: the heat channel is scaled down so cracks redden as they die; the card edges fade so rows blend into a wall
    const heat = f.heat.mul(B.x.mul(0.55).add(0.45)).sub(float(1).sub(B.x).mul(0.12));
    const ex = abs(uv().x.sub(0.5)).mul(2), edge = float(1).sub(smoothstep(0.72, 1.0, ex));
    m.colorNode = fireColour(heat, f.ember.mul(B.x), f.alpha);
    m.opacityNode = celAlpha(f.alpha.mul(edge.mul(0.5).add(0.5)).mul(base)).mul(edge).mul(A.w);
    this.mesh = new THREE.InstancedMesh(geo, m, cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 4;
  }
  begin(): void { this.n = 0; }
  put(x: number, y: number, z: number, w: number, h: number, phase: number, alpha: number, heat: number, lean: number, sway: number, tiles: number): void {
    if (this.n >= this.cap || h < 0.05 || alpha < 0.02) return;
    this.mesh.setMatrixAt(this.n, _m.makeTranslation(x, y, z));
    const A = this.a.array as Float32Array, B = this.b.array as Float32Array, i = this.n * 4;
    A[i] = w; A[i + 1] = h; A[i + 2] = phase; A[i + 3] = alpha; B[i] = heat; B[i + 1] = lean; B[i + 2] = sway; B[i + 3] = tiles;
    this.n++;
  }
  end(): void {
    this.mesh.count = this.n; this.mesh.instanceMatrix.needsUpdate = true; this.a.needsUpdate = true; this.b.needsUpdate = true;
  }
}

/** Opaque cel embers streaming up (buoyant, turbulent, shrinking): small camera-facing diamonds, white-hot → amber → red. */
export class Embers {
  readonly mesh: THREE.InstancedMesh;
  private a: THREE.InstancedBufferAttribute; private d: Float32Array; private n = 0; private static S = 9; // x y z vx vy vz life max size
  constructor(readonly cap: number) {
    this.d = new Float32Array(cap * Embers.S);
    this.a = new THREE.InstancedBufferAttribute(new Float32Array(cap * 2), 2); this.a.setUsage(THREE.DynamicDrawUsage);
    const A = instancedBufferAttribute(this.a) as U, g = positionGeometry;
    const m = new THREE.MeshBasicNodeMaterial({ side: THREE.DoubleSide, fog: false, alphaTest: 0.5 });
    const up: U = (cameraWorldMatrix as U).mul(vec4(0, 1, 0, 0)).xyz, right: U = (cameraWorldMatrix as U).mul(vec4(1, 0, 0, 0)).xyz;
    m.positionNode = positionLocal.sub(g).add(right.mul(g.x.mul(A.x))).add(up.mul(g.y.mul(A.x).mul(1.8)));
    const life = A.y; // 1 → 0
    let c: U = mix(color(RED), color(ORANGE), smoothstep(0.15, 0.3, life));
    c = mix(c, color(AMBER), smoothstep(0.4, 0.55, life));
    c = mix(c, color(WHITE_HOT), smoothstep(0.7, 0.85, life));
    m.colorNode = c;
    m.opacityNode = step(abs(g.x).add(abs(g.y)), float(0.5)); // diamond cut-out
    this.mesh = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), m, cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = 5;
  }
  add(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, size: number): void {
    if (this.n >= this.cap) return;
    const i = this.n++ * Embers.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = vx; d[i + 4] = vy; d[i + 5] = vz; d[i + 6] = life; d[i + 7] = life; d[i + 8] = size;
  }
  clear(): void { this.n = 0; this.mesh.count = 0; }
  update(dt: number, clock: number): void {
    const d = this.d, S = Embers.S, A = this.a.array as Float32Array;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 6] -= dt;
      if (d[i + 6] <= 0) { this.n--; if (k !== this.n) d.copyWithin(i, this.n * S, this.n * S + S); k--; continue; }
      // buoyant rise with drag and a curling sideways turbulence
      const dr = Math.exp(-1.8 * dt), sw = Math.sin(clock * 7 + i * 0.37) * 6 * dt;
      d[i + 3] = d[i + 3] * dr + sw; d[i + 5] = d[i + 5] * dr + Math.cos(clock * 6 + i * 0.53) * 6 * dt; d[i + 4] = d[i + 4] * dr + 7 * dt;
      d[i] += d[i + 3] * dt; d[i + 1] += d[i + 4] * dt; d[i + 2] += d[i + 5] * dt;
    }
    for (let k = 0; k < this.n; k++) {
      const i = k * S, u = d[i + 6] / d[i + 7];
      this.mesh.setMatrixAt(k, _m.makeTranslation(d[i], d[i + 1], d[i + 2]));
      A[k * 2] = d[i + 8] * (0.35 + 0.65 * u); A[k * 2 + 1] = u;
    }
    this.mesh.count = this.n; this.mesh.instanceMatrix.needsUpdate = true; this.a.needsUpdate = true;
  }
}

/** World-fixed spikes dropped behind the moving wall: they snap up, then sink back into the ground and cool. */
export class Trail {
  private d: Float32Array; private n = 0; private static S = 12; // x y z yaw lean sx h sz life max teal phase
  constructor(readonly cap: number) { this.d = new Float32Array(cap * Trail.S); }
  add(x: number, y: number, z: number, yaw: number, lean: number, sx: number, h: number, sz: number, life: number, teal: number): void {
    if (this.n >= this.cap) return;
    const i = this.n++ * Trail.S, d = this.d;
    d[i] = x; d[i + 1] = y; d[i + 2] = z; d[i + 3] = yaw; d[i + 4] = lean; d[i + 5] = sx; d[i + 6] = h; d[i + 7] = sz; d[i + 8] = life; d[i + 9] = life; d[i + 10] = teal; d[i + 11] = Math.random();
  }
  clear(): void { this.n = 0; }
  update(dt: number, out: Spikes): void {
    const d = this.d, S = Trail.S;
    for (let k = 0; k < this.n; k++) {
      const i = k * S; d[i + 8] -= dt;
      if (d[i + 8] <= 0) { this.n--; if (k !== this.n) d.copyWithin(i, this.n * S, this.n * S + S); k--; continue; }
      const u = 1 - d[i + 8] / d[i + 9];
      const g = u < 0.12 ? u / 0.12 : 1 - (u - 0.12) / 0.88, h = d[i + 6] * Math.max(0, g) ** 0.8;
      out.put(d[i], d[i + 1], d[i + 2], d[i + 3], d[i + 4] - u * 0.7, d[i + 5] * (1 - u * 0.3), h, d[i + 7], d[i + 10], 1 - u * 0.85, d[i + 11]);
    }
  }
}

/** Generic pooled flat decal: a plane in XZ with per-instance (age 0..1, a, b, c) data, drawn by a shared material. */
export class Decals {
  readonly mesh: THREE.InstancedMesh;
  readonly data: THREE.InstancedBufferAttribute;
  readonly items: { x: number; y: number; z: number; yaw: number; sx: number; sz: number; t: number; life: number; a: number; b: number; c: number }[] = [];
  /** live decals are items[0 .. live) */
  live = 0;
  constructor(private cap: number, geo: THREE.BufferGeometry, build: (d: U) => THREE.MeshBasicNodeMaterial, order: number) {
    this.data = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    this.mesh = new THREE.InstancedMesh(geo, build(instancedBufferAttribute(this.data)), cap);
    this.mesh.frustumCulled = false; this.mesh.count = 0; this.mesh.renderOrder = order;
    for (let i = 0; i < cap; i++) this.items.push({ x: 0, y: 0, z: 0, yaw: 0, sx: 1, sz: 1, t: 1, life: 0, a: 0, b: 0, c: 0 });
  }
  add(x: number, y: number, z: number, yaw: number, sx: number, sz: number, life: number, a = 0, b = 0, c = 0): void {
    if (this.live >= this.cap) { // recycle the oldest
      let o = 0; for (let k = 1; k < this.live; k++) if (this.items[k].t / this.items[k].life > this.items[o].t / this.items[o].life) o = k;
      const it = this.items[o]; this.items[o] = this.items[this.live - 1]; this.items[this.live - 1] = it; this.live--;
    }
    const it = this.items[this.live++];
    it.x = x; it.y = y; it.z = z; it.yaw = yaw; it.sx = sx; it.sz = sz; it.t = 0; it.life = life; it.a = a; it.b = b; it.c = c;
  }
  clear(): void { this.live = 0; this.mesh.count = 0; }
  update(dt: number): void {
    const D = this.data.array as Float32Array;
    for (let k = 0; k < this.live; k++) {
      const it = this.items[k]; it.t += dt;
      if (it.t >= it.life) { this.live--; this.items[k] = this.items[this.live]; this.items[this.live] = it; k--; }
    }
    for (let k = 0; k < this.live; k++) {
      const it = this.items[k];
      _e.set(0, it.yaw, 0); _q.setFromEuler(_e); _p.set(it.x, it.y, it.z); _s.set(it.sx, 1, it.sz);
      this.mesh.setMatrixAt(k, _m.compose(_p, _q, _s));
      D[k * 4] = it.t / it.life; D[k * 4 + 1] = it.a; D[k * 4 + 2] = it.b; D[k * 4 + 3] = it.c;
    }
    this.mesh.count = this.live; this.mesh.instanceMatrix.needsUpdate = true; this.data.needsUpdate = true;
  }
}

/** Flat XZ plane, local +z = forward; q.x across, q.y forward, both in [-1, 1]. */
export function flatPlane(): THREE.BufferGeometry { return new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2); }
export const qOf = (): U => vec2(uv().x.sub(0.5).mul(2), float(0.5).sub(uv().y).mul(2));

/** Burning crack across the ground: d = (age, seed, length in m, start heat). A jagged glowing core inside a charred
 *  band with short side cracks; heat pulses along the core like flowing embers, then it cools to dark and fades. */
export function crackMaterial(d: U): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, fog: false, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const age = d.x, seed = d.y, L = d.z, heat0 = d.w, q = qOf();
  const xm = q.x.mul(L).mul(0.5);
  const off = n2(xm.mul(0.55), seed).mul(0.32).add(n2(xm.mul(1.9), seed.add(3.1)).mul(0.12));
  const dy = abs(q.y.sub(off));
  const ends = smoothstep(1.0, 0.86, abs(q.x));
  const coreW = float(0.14).add(n2(xm.mul(1.4), seed.add(5.2)).mul(0.05)).mul(ends);
  const charW = float(0.62).add(n2(xm.mul(0.8), seed.add(8.7)).mul(0.16)).mul(ends).mul(float(1).sub(age.mul(0.3)));
  // side cracks: one candidate per 0.9 m cell, slanted, reaching out of the char band
  const cellN = xm.div(0.9), cell = cellN.floor(), side = step(off, q.y);
  const h = n2(cell.mul(3.7), seed.add(side.mul(5))).mul(0.5).add(0.5);
  const cx = cellN.sub(cell).sub(0.5).sub(dy.mul(h.sub(0.5)).mul(1.4));
  const reach = float(0.45).add(h.mul(0.45));
  const branch = step(abs(cx), float(0.05).mul(float(1).sub(dy.div(reach)).max(0))).mul(step(dy, reach)).mul(step(float(0.55), h)).mul(ends);
  const heat = heat0.mul(float(1).sub(age.mul(1.15)).clamp(0, 1));
  // molten flow: bright pulses travel along the core in both directions, plus a fast flicker
  const flow = n2(xm.mul(1.3).sub(time.mul(2.6)), seed.add(2)).add(n2(xm.mul(2.1).add(time.mul(1.9)), seed.add(6))).mul(0.25);
  const flick = n2(xm.mul(2.6), time.mul(7).add(seed)).mul(0.15).add(0.92).add(flow);
  const inCore = float(1).sub(smoothstep(coreW.mul(0.6), coreW, dy));
  const inChar = float(1).sub(smoothstep(charW.mul(0.75), charW, dy));
  const glowRing = float(1).sub(smoothstep(coreW, coreW.mul(1.9), dy)).mul(heat);
  let hot: U = mix(color(RED), color(ORANGE), smoothstep(0.15, 0.45, heat));
  hot = mix(hot, color(AMBER), smoothstep(0.45, 0.75, heat));
  hot = mix(hot, color(PALE), smoothstep(0.9, 1.0, heat).mul(0.6));
  let col: U = mix(color(CHAR), color(RED).mul(0.85), glowRing);
  col = mix(col, hot.mul(flick), max(inCore, branch.mul(heat).mul(0.8)).mul(smoothstep(0.02, 0.12, heat)));
  col = mix(col, color(CHAR), inCore.mul(float(1).sub(smoothstep(0.0, 0.1, heat))));
  m.colorNode = col;
  const fade = float(1).sub(smoothstep(0.45, 1.0, age));
  m.opacityNode = max(max(inCore, branch), inChar).mul(fade);
  return m;
}

/** Fire curtain: d = (alpha, seed, width in m, 0). The same flipbook fire as the cards, tiled across the wall, rippling
 *  (vertex wave) as it rolls. */
export function sheetMaterial(d: U, tex: AuraTextures): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const alpha = d.x, seed = d.y, W = d.z;
  const u = uv().x, v = uv().y;
  // rolling ripple: the curl breathes forward and back along the wall, more toward the top (world units, after instancing)
  const rip = sin(time.mul(7.0).add(u.mul(W).mul(1.1)).add(seed.mul(5))).mul(0.5).add(sin(time.mul(11.0).sub(u.mul(W).mul(0.7))).mul(0.3));
  m.positionNode = positionLocal.add(UP.mul(rip.mul(v).mul(0.25)));
  const f = fireAt(tex, u.mul(W.div(2.4)).add(seed.mul(0.13)), v, fract(seed.mul(0.37)), 14);
  const xe = u.mul(2).sub(1), edge = float(1).sub(smoothstep(0.75, 1.0, abs(xe)));
  m.colorNode = fireColour(f.heat, f.ember, f.alpha);
  m.opacityNode = celAlpha(f.alpha).mul(edge).mul(alpha);
  return m;
}

/** a pool of `cap` fire curtains (sheetGeometry + sheetMaterial) with their per-instance data (alpha, seed, width, 0);
 *  shared with the Magma Front (magmaFront.ts), which curls its own curtain over the wall's foot */
export function instancedSheet(tex: AuraTextures, cap: number): { mesh: THREE.InstancedMesh; data: THREE.InstancedBufferAttribute } {
  const data = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4); data.setUsage(THREE.DynamicDrawUsage);
  const mesh = new THREE.InstancedMesh(sheetGeometry(24, 8), sheetMaterial(instancedBufferAttribute(data), tex), cap);
  mesh.frustumCulled = false; mesh.count = 0; mesh.renderOrder = 3;
  return { mesh, data };
}

/** Lead crest rows, 5 numbers each: offset along travel (m), height factor, forward lean (rad), half-step shift, teal.
 *  Low fire teeth in front, the tall fire crest, a teal energy row, a low fire back row. */
const ROWS = [0.65, 0.42, 0.75, 0.5, 0, 0, 1, 0.38, 0, 0, -0.6, 0.8, 0.22, 0.5, 1, -1.2, 0.55, 0.05, 0.25, 0];
/** Flame card rows riding the wall, 4 numbers each: offset along travel (m), height factor (× wall height), width (m), lean */
const CARD_ROWS = [-0.15, 1.3, 2.4, 0.4, -1.05, 0.95, 2.0, 0.3];

/** One live (or collapsing) wall, mirroring a sim wall by id. */
interface Vis {
  on: boolean; alive: boolean; id: number; t: number; post: number;
  x: number; z: number; dx: number; dz: number; w: number; H: number; seed: number; evo: boolean; back: boolean;
  trailAcc: number; dustAcc: number; chipAcc: number; emberAcc: number;
}

export class RiftlineFx {
  private spikes: Spikes; private trail: Trail; private cards: FlameCards; private embers: Embers;
  private sheet: THREE.InstancedMesh; private sheetData: THREE.InstancedBufferAttribute;
  private cracks: Decals;
  private vis: Vis[] = [];
  private readonly qk: number;
  private clock = 0; private frameHits = 0; private lightT = 0; private crackEmberAcc = 0;
  private recent = new Float32Array(16 * 3).fill(-99); private recentI = 0;
  /** frames left to draw every layer invisibly so shaders compile at load, not on the first cast */
  private warm = 3;

  constructor(scene: THREE.Scene, quality: 'low' | 'medium' | 'high', private fx: Fx, private groundAt: (x: number, z: number) => number) {
    this.qk = quality === 'low' ? 0.5 : quality === 'medium' ? 0.75 : 1;
    const tex = auraTextures();
    this.spikes = new Spikes(Math.round(560 * Math.max(0.7, this.qk)));
    this.trail = new Trail(Math.round(260 * this.qk));
    this.cards = new FlameCards(260, tex);
    this.embers = new Embers(Math.round(420 * Math.max(0.5, this.qk)));
    this.sheetData = new THREE.InstancedBufferAttribute(new Float32Array(6 * 4), 4); this.sheetData.setUsage(THREE.DynamicDrawUsage);
    this.sheet = new THREE.InstancedMesh(sheetGeometry(24, 8), sheetMaterial(instancedBufferAttribute(this.sheetData), tex), 6);
    this.sheet.frustumCulled = false; this.sheet.count = 0; this.sheet.renderOrder = 3;
    this.cracks = new Decals(10, flatPlane(), crackMaterial, 1);
    for (let i = 0; i < 6; i++) this.vis.push({ on: false, alive: false, id: -1, t: 0, post: 0, x: 0, z: 0, dx: 1, dz: 0, w: 8, H: 2.4, seed: 0, evo: false, back: false, trailAcc: 0, dustAcc: 0, chipAcc: 0, emberAcc: 0 });
    scene.add(this.spikes.ink, this.spikes.mesh, this.sheet, this.cracks.mesh, this.cards.mesh, this.embers.mesh);
  }

  // ------------------------------------------------------------------ events
  /** Cast (`riftStart`): the walls themselves are picked up from sim.rift.walls in update(); this is the release cue. */
  start(sim: Sim, hx: number, hz: number): void {
    const a = sim.rift.a, gy = this.groundAt(hx, hz);
    this.fx.stars.add(hx + Math.cos(a) * 1.1, gy + 0.6, hz + Math.sin(a) * 1.1, 1.4, 0.1, AMBER, 2.2);
  }

  /** A real Riftline damage event: a small spark burst thrown along the wall's travel. Crowds are aggregated. */
  hit(x: number, y: number, z: number, crit: boolean, hx: number, hz: number): void {
    const R = this.recent;
    for (let k = 0; k < 16; k++) {
      const ddx = R[k * 3] - x, ddz = R[k * 3 + 1] - z;
      if (this.clock - R[k * 3 + 2] < 0.12 && ddx * ddx + ddz * ddz < 1.2) return;
    }
    const budget = Math.max(2, Math.round(8 * this.qk * (1 - CROWD.busy * 0.6)));
    if (this.frameHits >= budget) return;
    this.frameHits++;
    const i = this.recentI; this.recentI = (i + 1) % 16; R[i * 3] = x; R[i * 3 + 1] = z; R[i * 3 + 2] = this.clock;
    // travel direction of the nearest live wall (else away from the hero)
    let dx = x - hx, dz = z - hz, best = 1e9;
    for (const v of this.vis) if (v.on) { const d = (v.x - x) ** 2 + (v.z - z) ** 2; if (d < best) { best = d; dx = v.dx; dz = v.dz; } }
    const l = Math.hypot(dx, dz) || 1; dx /= l; dz /= l;
    const gy = this.groundAt(x, z), n = crit ? 6 : 4;
    for (let k = 0; k < n; k++) {
      const sp = 6 + Math.random() * 7, sx = (Math.random() - 0.5) * 5;
      this.fx.particles.emitDir(x, gy + 0.8 + Math.random() * 0.6, z, dx * sp - dz * sx, 2 + Math.random() * 4, dz * sp + dx * sx, k % 3 === 0 ? PALE : k % 3 === 1 ? AMBER : TEAL, 0.32, 0.22 + Math.random() * 0.12, 3);
      this.embers.add(x, gy + 0.8 + Math.random() * 0.6, z, dx * sp * 0.6 - dz * sx, 3 + Math.random() * 4, dz * sp * 0.6 + dx * sx, 0.35 + Math.random() * 0.25, 0.14 + Math.random() * 0.06);
    }
    if (crit) this.fx.stars.add(x, gy + 1, z, 1.3, 0.1, PALE, 2.5);
    if (this.lightT <= 0 && FLASH.dim >= 1) { this.fx.lights.flash(x, gy + 0.8, z, ORANGE, 10, 0.12, 6); this.lightT = 0.1; }
  }

  // ------------------------------------------------------------------ per frame
  update(dt: number, sim: Sim, _hx: number, _hz: number): void {
    this.clock += dt; this.frameHits = 0; this.lightT -= dt;
    const walls = sim.rift.walls;
    // follow the sim walls by id; new ids erupt, vanished ids collapse
    for (const v of this.vis) if (v.on && v.alive) {
      let found = false;
      for (const w of walls) if (w.id === v.id) { found = true; v.x = w.x; v.z = w.z; break; }
      if (!found) { v.alive = false; v.post = 0; this.collapse(v); }
    }
    for (const w of walls) {
      let known = false;
      for (const v of this.vis) if (v.on && v.id === w.id) { known = true; break; }
      if (!known) this.erupt(sim, w);
    }
    this.spikes.begin(); this.cards.begin();
    let ns = 0; const SD = this.sheetData.array as Float32Array;
    for (const v of this.vis) {
      if (!v.on) continue;
      if (v.alive) v.t += dt; else { v.post += dt; if (v.post > 0.3) { v.on = false; continue; } }
      ns = this.drawWall(v, dt, ns, SD);
    }
    this.sheet.count = ns; this.sheet.instanceMatrix.needsUpdate = true; this.sheetData.needsUpdate = true;
    this.crackFire(dt);
    this.trail.update(dt, this.spikes);
    this.spikes.end(); this.cards.end();
    this.embers.update(dt, this.clock);
    this.cracks.update(dt);
    if (this.warm > 0) { // compile pass: one degenerate instance of each layer
      this.warm--;
      for (const o of [this.spikes.mesh, this.sheet, this.cracks.mesh, this.cards.mesh, this.embers.mesh]) if (o.count === 0) { o.setMatrixAt(0, _m.makeScale(0, 0, 0)); o.count = 1; o.instanceMatrix.needsUpdate = true; }
      this.spikes.ink.count = this.spikes.mesh.count;
    }
  }

  /** Flame cards licking along every hot crack (they shrink and redden as it cools) and embers rising off it. */
  private crackFire(dt: number): void {
    const busyK = Math.max(0.3, 1 - CROWD.busy);
    for (let ci = 0; ci < this.cracks.live; ci++) {
      const it = this.cracks.items[ci], u = it.t / it.life, heat = it.c * Math.max(0, 1 - u * 1.05);
      if (heat <= 0.03) continue;
      const ax = Math.cos(it.yaw), az = -Math.sin(it.yaw), fx = Math.sin(it.yaw), fz = Math.cos(it.yaw), L = it.sx * 2 * 0.9, nc = Math.max(3, Math.round(L / 0.8));
      const grow = Math.min(1, it.t / 0.12), alpha = Math.min(1, heat * 3) * grow;
      for (let k = 0; k < nc; k++) {
        // jittered along and across the crack (no ladder of aligned cards); bases sunk so the solid skirt meets the ground
        const r = hash1(k * 7.7 + it.a * 13), r2 = hash1(k * 3.3 + it.a * 7 + 1), o = ((k + 0.5) / nc - 0.5) * L + (r - 0.5) * 0.45, off = (r2 - 0.5) * 0.45;
        const e = 1 - (2 * o / L) ** 2, px = it.x + ax * o + fx * off, pz = it.z + az * o + fz * off;
        const h = (0.8 + 1.1 * heat) * (0.6 + 0.7 * r2) * (0.4 + 0.6 * Math.sqrt(Math.max(0, e))) * grow;
        this.cards.put(px, this.groundAt(px, pz) - 0.22, pz, 1.35, h + 0.22, fract1(r + it.a * 0.31), alpha, 0.35 + 0.65 * heat, 0.45, 0.3, 0.55);
      }
      this.crackEmberAcc += dt * 26 * heat * this.qk * busyK;
      while (this.crackEmberAcc >= 1) {
        this.crackEmberAcc -= 1;
        const o = (Math.random() - 0.5) * L, px = it.x + ax * o, pz = it.z + az * o;
        this.embers.add(px, this.groundAt(px, pz) + 0.3 + Math.random() * 0.5, pz, (Math.random() - 0.5) * 1.5, 2 + Math.random() * 3, (Math.random() - 0.5) * 1.5, 0.6 + Math.random() * 0.5, 0.1 + Math.random() * 0.07);
      }
    }
  }

  private erupt(sim: Sim, wall: RiftWall): void {
    const { id, dx, dz, w, back } = wall, x = wall.x0, z = wall.z0, wave2 = wall.dmg < sim.rift.dmg * 0.99; // split line = spawn spot
    let v = this.vis.find((o) => !o.on);
    if (!v) { v = this.vis[0]; for (const o of this.vis) if (o.post > v.post || (!o.alive && v.alive)) v = o; }
    const rank = Math.max(1, sim.wlv('riftline')), evo = sim.evolved.has('riftline');
    Object.assign(v, { on: true, alive: true, id, t: 0, post: 0, x: wall.x, z: wall.z, dx, dz, w, evo, back, seed: (id * 0.618) % 1,
      // owner 2026-11-02: 'as you level up the rift line, make the flames get taller and taller' — 3 m at rank 1 → 5.2 m at rank 5, 6.4 m evolved
      H: (3.0 + 0.55 * (rank - 1) + (evo ? 1.2 : 0)) * (wave2 ? 0.85 : 1), trailAcc: 0, dustAcc: 0, chipAcc: 0, emberAcc: 0 });
    const gy = this.groundAt(x, z), yaw = Math.atan2(dx, dz);
    // the split: a burning crack across the front (the second wave rides the same crack)
    if (!wave2) this.cracks.add(x, gy + 0.04, z, yaw, w * 0.56, 1.05, 2.2, v.seed * 10, w * 1.12, 1);
    // eruption burst: chips, dust and a fountain of embers along the split line
    const n = Math.round(w * 1.1 * this.qk);
    for (let k = 0; k < n; k++) {
      const o = (Math.random() - 0.5) * w, px = x - dz * o, pz = z + dx * o;
      if (k % 2 === 0) this.fx.blast?.chips(px, pz, dx, dz, 1);
      if (k % 3 === 0) this.fx.blast?.dust(px, gy + 0.3, pz, dx * 3, 1.5, dz * 3, 1.0 + Math.random() * 0.4, 0.8, 2.6, Math.random() * 0.5);
      this.embers.add(px, gy + 0.3, pz, dx * 2 + (Math.random() - 0.5) * 3, 6 + Math.random() * 6, dz * 2 + (Math.random() - 0.5) * 3, 0.5 + Math.random() * 0.5, 0.14 + Math.random() * 0.08);
    }
    if (FLASH.dim >= 1) this.fx.lights.flash(x, gy + 1.2, z, ORANGE, 26, 0.3, 12);
    this.fx.shake = Math.min(1, this.fx.shake + (wave2 ? 0.08 : 0.16));
  }

  /** The wall reached the end of its run: a last spray of chips, dust and embers and a smaller cooling crack where it sank. */
  private collapse(v: Vis): void {
    const gy = this.groundAt(v.x, v.z), yaw = Math.atan2(v.dx, v.dz);
    this.cracks.add(v.x + v.dx * 0.3, gy + 0.04, v.z + v.dz * 0.3, yaw, v.w * 0.5, 0.7, 1.4, v.seed * 10 + 3, v.w, 0.55);
    const n = Math.round(v.w * 0.9 * this.qk);
    for (let k = 0; k < n; k++) {
      const o = (Math.random() - 0.5) * v.w, px = v.x - v.dz * o, pz = v.z + v.dx * o;
      if (k % 2 === 0) this.fx.blast?.chips(px, pz, v.dx, v.dz, 1);
      if (k % 2 === 1) this.fx.blast?.dust(px, gy + 0.4, pz, v.dx * 4, 1.2, v.dz * 4, 1.1 + Math.random() * 0.4, 1, 2.8, 0.3 + Math.random() * 0.4);
      this.embers.add(px, gy + 0.5 + Math.random() * v.H * 0.6, pz, v.dx * 5 + (Math.random() - 0.5) * 3, 3 + Math.random() * 5, v.dz * 5 + (Math.random() - 0.5) * 3, 0.6 + Math.random() * 0.5, 0.14 + Math.random() * 0.08);
    }
  }

  private drawWall(v: Vis, dt: number, ns: number, SD: Float32Array): number {
    const { dx, dz, w } = v, ax = -dz, az = dx, yaw = Math.atan2(dx, dz);
    // eruption: spikes punch up with a little overshoot in ~0.1 s; collapse: they sink in ~0.22 s
    const te = Math.min(1, v.t / 0.1), env = v.alive ? (te < 1 ? 1 - (1 - te) ** 3 * (1 - 2.2 * te) : 1) : Math.max(0, 1 - v.post / 0.22) ** 1.5;
    const H = v.H * env, n = Math.max(6, Math.round(w / 0.5)), step = w / n;
    const busyK = Math.max(0.35, 1 - CROWD.busy * 0.7);
    // four staggered spike rows (ROWS): the wall's cross-section reads as a wave from any side; the shader animates them
    for (let ri = 0; ri < ROWS.length; ri += 5) for (let j = 0; j < n; j++) {
      const off = ROWS[ri], hk = ROWS[ri + 1], lean = ROWS[ri + 2], sh = ROWS[ri + 3], teal = ROWS[ri + 4];
      const r = hash1(j * 3.1 + off * 17 + v.seed * 91), o = ((j + 0.5 + sh * (r - 0.5)) / n - 0.5) * w;
      const e = 1 - (2 * o / w) ** 2, taper = 0.35 + 0.65 * Math.sqrt(Math.max(0, e));
      const px = v.x + ax * o + dx * off, pz = v.z + az * o + dz * off;
      this.spikes.put(px, this.groundAt(px, pz) - 0.05, pz, yaw + (r - 0.5) * 0.5, lean + (r - 0.5) * 0.2, step * (1.35 + 0.35 * r), H * hk * 0.8 * (0.62 + 0.38 * r) * taper, 0.8 + 0.3 * r, teal, 1, r);
    }
    // living fire: flame card rows riding the crest (camera-facing, so the fire reads whichever way the wall travels)
    const calpha = v.alive ? Math.min(1, v.t / 0.05) : Math.max(0, 1 - v.post / 0.28);
    const cH = v.H * (v.alive ? env : Math.max(0, 1 - v.post / 0.3));
    for (let ri = 0; ri < CARD_ROWS.length; ri += 4) {
      const off = CARD_ROWS[ri], hk = CARD_ROWS[ri + 1], cw = CARD_ROWS[ri + 2], lean = CARD_ROWS[ri + 3];
      const nc = Math.max(4, Math.ceil(w / (cw * 0.62)));
      for (let j = 0; j < nc; j++) {
        const r = hash1(j * 5.3 + ri * 11 + v.seed * 57), o = ((j + 0.5) / nc - 0.5) * w + (r - 0.5) * 0.4;
        const e = 1 - (2 * o / w) ** 2, taper = 0.3 + 0.7 * Math.sqrt(Math.max(0, e));
        const px = v.x + ax * o + dx * off, pz = v.z + az * o + dz * off;
        this.cards.put(px, this.groundAt(px, pz) - 0.1, pz, cw, cH * hk * (0.8 + 0.35 * r) * taper, fract1(r + v.seed), calpha, 1, lean, 0.35, 0.85);
      }
    }
    // fire curtain riding just behind the crest
    if (ns < 6) {
      const gy = this.groundAt(v.x, v.z);
      _e.set(0, yaw, 0); _q.setFromEuler(_e); _p.set(v.x - dx * 0.7, gy - 0.05, v.z - dz * 0.7); _s.set(w * 1.02, H * 1.15, 2.2);
      this.sheet.setMatrixAt(ns, _m.compose(_p, _q, _s));
      SD[ns * 4] = v.alive ? Math.min(1, v.t / 0.06) : Math.max(0, 1 - v.post / 0.2); SD[ns * 4 + 1] = v.seed * 10; SD[ns * 4 + 2] = w; SD[ns * 4 + 3] = 0;
      ns++;
    }
    if (!v.alive) return ns;
    // rolling tail: world-fixed spikes dropped at the wall face that sink behind it
    v.trailAcc += dt;
    if (v.trailAcc >= 0.035) {
      v.trailAcc = 0;
      const k = Math.round(n * 0.45 * this.qk * busyK);
      for (let i = 0; i < k; i++) {
        const o = (Math.random() - 0.5) * w * 0.95, e = 1 - (2 * o / w) ** 2, taper = 0.35 + 0.65 * Math.sqrt(Math.max(0, e));
        const px = v.x + ax * o - dx * 0.25, pz = v.z + az * o - dz * 0.25;
        this.trail.add(px, this.groundAt(px, pz) - 0.05, pz, yaw + (Math.random() - 0.5) * 0.25, 0.3, step * 1.5, v.H * (0.4 + Math.random() * 0.3) * taper, 0.8, 0.17 + Math.random() * 0.08, Math.random() < 0.3 ? 1 : 0);
      }
    }
    // dust rolling ahead of the base and rock chips thrown forward
    v.dustAcc += dt * 34 * this.qk * busyK;
    while (v.dustAcc >= 1) {
      v.dustAcc -= 1;
      const o = (Math.random() - 0.5) * w, px = v.x + ax * o + dx * 0.4, pz = v.z + az * o + dz * 0.4;
      this.fx.blast?.dust(px, this.groundAt(px, pz) + 0.25, pz, dx * (9 + Math.random() * 4) + ax * (Math.random() - 0.5) * 2, 0.8 + Math.random(), dz * (9 + Math.random() * 4) + az * (Math.random() - 0.5) * 2, 0.6 + Math.random() * 0.35, 0.5, 1.9, Math.random() * 0.35);
    }
    v.chipAcc += dt * 22 * this.qk * busyK;
    while (v.chipAcc >= 1) {
      v.chipAcc -= 1;
      const o = (Math.random() - 0.5) * w;
      this.fx.blast?.chips(v.x + ax * o + dx * 0.5, v.z + az * o + dz * 0.5, dx, dz, 1);
    }
    // embers streaming up off the crest (opaque cel sparks) plus a few additive glints for the Glow setting
    v.emberAcc += dt * 85 * this.qk * busyK;
    while (v.emberAcc >= 1) {
      v.emberAcc -= 1;
      const o = (Math.random() - 0.5) * w * 0.95, px = v.x + ax * o - dx * 0.4, pz = v.z + az * o - dz * 0.4, gy = this.groundAt(px, pz);
      this.embers.add(px, gy + v.H * (0.5 + Math.random() * 0.8), pz, dx * (6 + Math.random() * 6) + ax * (Math.random() - 0.5) * 2, 3 + Math.random() * 5, dz * (6 + Math.random() * 6) + az * (Math.random() - 0.5) * 2, 0.45 + Math.random() * 0.5, 0.12 + Math.random() * 0.1);
      if (Math.random() < 0.25) this.fx.particles.emitDir(px, gy + v.H * (0.7 + Math.random() * 0.4), pz, dx * 10 + (Math.random() - 0.5) * 3, 3 + Math.random() * 3, dz * 10 + (Math.random() - 0.5) * 3, AMBER, 0.28, 0.3, 3);
    }
    return ns;
  }

  /** Mark fresh scorches along the split line (render-side flag) so props.ts skips its broad ember glow there: the crack
   *  decal and its flames here draw the burning line. The char mark in the ground burn map is kept. */
  tagScorches(sim: Sim): void {
    if (!sim.rift.active) return;
    for (const sc of sim.terrain.scorches) {
      if (sim.tick - sc.born > 3) continue;
      const tg = sc as { _rift?: boolean }; if (tg._rift) continue;
      for (const w of sim.rift.walls) {
        const rx = sc.x - w.x0, rz = sc.z - w.z0;
        if (Math.abs(rx * w.dx + rz * w.dz) < 0.5 && Math.abs(rx * -w.dz + rz * w.dx) < w.w / 2 + 1) { tg._rift = true; break; }
      }
    }
  }

  clear(): void {
    for (const v of this.vis) { v.on = false; v.alive = false; }
    this.trail.clear(); this.cracks.clear(); this.embers.clear();
    this.spikes.begin(); this.spikes.end(); this.cards.begin(); this.cards.end(); this.sheet.count = 0;
  }
}

export const fract1 = (x: number): number => x - Math.floor(x);
